import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { setCompensation } from 'src/game/inventory/inventory.redux';
import { IAPAnalytics } from './analytics/analytics.types';
import {
  trackPurchaseSupportRequest,
  trackHidePurchaseComplete,
  trackPurchaseSupportReject,
  trackShowPurchaseCanceled,
  trackShowPurchaseComplete,
  trackShowPurchaseFailed,
} from './analytics/iap.purchase.complete';
import { showSupportWidget } from 'src/lib/CSWidget';
import { Feature } from 'src/replicant/analytics';
import { withLoading } from 'src/lib/utils';
import { ProductId } from 'src/replicant/iap';
import * as Iap from 'src/replicant/iap';
import StateObserver, { user } from 'src/StateObserver';
import { getRewards } from 'src/replicant/match2/shopbundles/match2-shopbundles.getters';
import { Match2Reward } from 'src/replicant/match2/rewards/match2-rewards.ruleset';
import {
  openPurchaseFailurePopup,
  type PurchaseFailureType,
} from './iap.purchase.failure.popup';

/**
 * Handles the completion of a purchase animation for a given product.
 *
 * @param productId - The ID of the product that was purchased.
 * @param analytics - The revenue analytics data for the purchase.
 *
 * This function performs the following steps:
 * 1. Retrieves the current state and checks if the product is a shop bundle pack.
 * 2. If the product is a shop bundle pack, it processes the next bundle sale step.
 * 3. Retrieves the rewards associated with the product.
 * 4. Displays a popup animation showing the rewards.
 *
 * @returns A promise that resolves when the animation is complete.
 */
export async function purchaseCompleteSequence(
  productId: ProductId,
  analytics: IAPAnalytics,
) {
  // Track animation start
  trackShowPurchaseComplete(analytics);

  const now = StateObserver.now();
  const { paidGems = 0, freeGems = 0 } = Iap.utils.getProductRewards(
    productId,
    user(),
    now,
  );

  // Get rewards list, bundle sale has own get rewards method
  const rewardsList = getRewards(productId, now);

  // For some feature we want to skip rewards animations after popup close
  const skipCloseAnimFeatures = [
    'sale',
    'puzzle_booster',
    'match_continue',
    'gacha',
    'piggy_bank',
    'magicLamps',
  ];

  // Prepare rewards list
  const gemsReward = paidGems + freeGems;
  const rewards = gemsReward
    ? ([...rewardsList, { type: 'gems', amount: gemsReward }] as Match2Reward[])
    : rewardsList;

  // Show rewards popup animation
  await openPopupPromise('popupNewPurchaseComplete', {
    rewards,
    skipShowAnimation: false,
    skipCloseAnimation: skipCloseAnimFeatures.includes(analytics.feature),
  });

  // Track animation complete
  trackHidePurchaseComplete(analytics);
}

/**
 * Purchase canceled by user sequence
 */
export async function purchaseCanceledSequence(analytics: IAPAnalytics) {
  trackShowPurchaseCanceled(analytics);

  const needSupport = await openPopupPromise('popupPurchaseCanceled', {});

  if (needSupport) {
    trackPurchaseSupportRequest(analytics, {
      cancellationType: 'cancel',
    });

    await withLoading(() => showSupportWidget(), {
      feature: Feature.IAP._,
      subFeature: Feature.IAP.SHOW_SUPPORT_WIDGET_ON_CANCEL,
    });
  } else {
    trackPurchaseSupportReject(analytics, {
      cancellationType: 'cancel',
    });
  }
}

/**
 * Purchase failed sequence. Show error popup and support widget.
 * We call it on technical issues, not on user cancel.
 */
export async function purchaseFailedSequence(
  analytics: {
    feature: string;
    subFeature: string;
    productID: ProductId;
  },
  type: PurchaseFailureType,
) {
  trackShowPurchaseFailed(analytics);

  const needSupport = await openPurchaseFailurePopup(user(), type);

  if (needSupport) {
    trackPurchaseSupportRequest(analytics, {
      cancellationType: 'error',
    });

    await withLoading(() => showSupportWidget(), {
      feature: Feature.IAP._,
      subFeature: Feature.IAP.SHOW_SUPPORT_WIDGET_ON_ERROR,
    });
  } else {
    trackPurchaseSupportReject(analytics, {
      cancellationType: 'error',
    });
  }
}
